import { useMutation } from "@tanstack/react-query";
import { errorToaster, successToaster } from "components/pages/measurement-devices/utils/utils";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { queryClient } from "services/HttpService";
import { T } from "services/lang/LanguageService";
import DashboardApi from "../DashboardApi";
import {
  CreateDashboardRequest,
  Dashboard,
  DashboardPaginatedRequest,
  DashboardRequest,
  DashboardSimpleDto,
} from "../model/dashboard/Dashboard";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IMutationOptions,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

export const useGetDashboards = (options?: IQueryOptions) => {
  const company = useCurrentCompanyContext();
  return useQueryWrapper(
    [QueryKey.DASHBOARDS_MANAGEMENT, company?.companyReference!],
    () => DashboardApi.getDashboards(company?.companyReference!),
    null,
    options
  );
};

export const useGetAllDashboardsInfo = (options?: IQueryOptions) =>
  useQueryWrapper([QueryKey.DASHBOARD], () => DashboardApi.getAllDashboards(), null, options);

export const useGetDashboardsInfo = (dashboardsIds: Array<number>, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DASHBOARD, ...dashboardsIds.toString()],
    () => DashboardApi.getDashboardsInfos(dashboardsIds),
    null,
    options
  );

export const useGetDashboard = (dashboardId: number, options?: IQueryOptions<Dashboard>) =>
  useQueryWrapper(
    [QueryKey.DASHBOARD, dashboardId.toString()],
    () => DashboardApi.getDashboard(dashboardId),
    null,
    options
  );

export const useGetDashboardHistory = (
  request: DashboardPaginatedRequest,
  options?: IQueryOptions<Dashboard>
) =>
  useQueryWrapper(
    [
      QueryKey.DASHBOARD_HISTORY,
      request.dashboardId.toString(),
      request.skip?.toString(),
      request.take?.toString(),
    ],
    () => DashboardApi.getDashboardHistory(request),
    null,
    options
  );

export const useCreateDashboard = (specOptions?: IMutationOptions) => {
  const company = useCurrentCompanyContext();
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (dashboard: CreateDashboardRequest) => {
      const res = await DashboardApi.createDashboard({
        ...dashboard,
        companyRef: company?.companyReference!,
      });
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData(
        [QueryKey.DASHBOARDS_MANAGEMENT, data?.companyRef!],
        (oldData: DashboardSimpleDto[]) => {
          return oldData && [...oldData, data];
        }
      );
      successToaster(T("alert.action-successful"));
      options.onSuccess!!(data);
    },
    onError: error => {
      errorToaster(`${T("alert.failed-to-proceed")}: ${error.message}`);
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useUpdateDashboard = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (dashboard: DashboardRequest) => {
      const res = await DashboardApi.updateDashboard(dashboard, dashboard.overwrite);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.DASHBOARD, data!!.id.toString()], () => {
        return data;
      });
      queryClient.invalidateQueries({ queryKey: [QueryKey.DASHBOARD_HISTORY, data?.id?.toString()] });
      options.onSuccess!!(data);
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useUpdateManagedDashboard = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (dashboard: DashboardSimpleDto) => {
      const res = await DashboardApi.updateManagedDashboard(dashboard);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData(
        [QueryKey.DASHBOARDS_MANAGEMENT, data?.companyRef!],
        (oldData: DashboardSimpleDto[]) => {
          return oldData && oldData.map(d => (d.id === data?.id ? data : d));
        }
      );
      successToaster(T("alert.action-successful"));
      options.onSuccess!!(data);
    },
    onError: error => {
      errorToaster(`${T("alert.failed-to-proceed")}: ${error.message}`);
      options.onFailure!!(error);
    },
  });
};

export const useDeleteDashboard = () => {
  return useMutation({
    mutationFn: async (dashboardId: number) => {
      const res = await DashboardApi.deleteDashboard(dashboardId);
      return defaultResponseHandler(res);
    },
    onSuccess: (data, dashboardId) => {
      queryClient.setQueryData(
        [QueryKey.DASHBOARDS_MANAGEMENT, data?.companyRef!],
        (oldData: DashboardSimpleDto[]) => {
          return oldData && oldData.filter(dashboard => dashboard.id !== dashboardId);
        }
      );
      successToaster(T("alert.action-successful"));
    },
    onError: error => {
      errorToaster(`${T("alert.failed-to-proceed")}: ${error.message}`);
    },
  });
};
